/**
 * Variant ID submit guard.
 *
 * The theme updates `product-form input[name="id"]` only after an async section
 * fetch replaces the form, so adding to cart before that fetch resolves (or when
 * responses arrive out of order) could submit the previously selected variant.
 * The checked option radios are the single source of truth for what the customer
 * sees, so we resolve the matching variant from those and write it into the hidden
 * input during the capture phase of `submit`, before the theme's own handler reads
 * the form data. This covers both the main PDP and the quick-add modal.
 */
(() => {
  const VARIANTS_JSON_SELECTOR = '[data-target="variant-select.variants-json"]';
  const OPTION_FIELDSET_SELECTOR = '[data-target="variant-select.option"]';

  const parseVariants = (variantSelect) => {
    const json = variantSelect.querySelector(VARIANTS_JSON_SELECTOR);
    if (!json) return null;
    try {
      const variants = JSON.parse(json.textContent);
      return Array.isArray(variants) && variants.length ? variants : null;
    } catch {
      return null;
    }
  };

  // True when an option group (e.g. size) is rendered but has no checked radio,
  // so we defer to the theme instead of resolving an arbitrary variant.
  const hasUnselectedOption = (variantSelect) =>
    [...variantSelect.querySelectorAll(OPTION_FIELDSET_SELECTOR)].some((fieldset) => {
      const hasRadios = fieldset.querySelector('input[type="radio"]') !== null;
      return hasRadios && !fieldset.querySelector('input[type="radio"]:checked');
    });

  // Resolve the variant whose options exactly match the checked radios, mirroring
  // the theme's matching logic. Returns null when there is no confident match so
  // the server-rendered value stands.
  const resolveVariantId = (variantSelect) => {
    const optionsForm = variantSelect.querySelector('form');
    const variants = parseVariants(variantSelect);
    if (!optionsForm || !variants) return null;

    const selectedOptions = [...new FormData(optionsForm).entries()].filter(
      ([name]) => name.startsWith('option')
    );
    if (!selectedOptions.length) return null;

    const match = variants.find((variant) =>
      selectedOptions.every(([name, value]) => String(variant[name]) === value)
    );

    return match ? String(match.id) : null;
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const productForm = form.closest('product-form');
      if (!productForm) return;

      const variantDetails = productForm.closest('variant-details');
      const variantSelect = variantDetails?.querySelector('variant-select');
      if (!variantSelect || hasUnselectedOption(variantSelect)) return;

      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;

      const variantId = resolveVariantId(variantSelect);
      if (variantId && idInput.value !== variantId) {
        idInput.value = variantId;
      }
    },
    true
  );
})();
